import { Injectable, inject } from '@angular/core';

import { AppConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { DeviceService, EventsService, HtmlNode, VanillaEventNames, ViewTransitionUtils } from '@frontend/vanilla/core';
import { ProductMenuService } from '@frontend/vanilla/shared/product-menu';
import { filter, take, takeUntil } from 'rxjs';

import { ModalGestureBackgroundService } from './modal-gesture-background.service';
import { ModalGestureEvents } from './modal-gesture-events';
import { ModalGestureHelperService } from './modal-gesture-helper.service';
import { ModalGestureQueriesService } from './modal-gesture-queries.service';
import { ModalGestureTransformOriginService } from './modal-gesture-transform-origin.service';
import {
    ANIMATION_COMPLETE_RATIO_VERTICAL,
    CLOSING_DURATION_HORIZONTAL,
    CLOSING_DURATION_VERTICAL,
    CLOSING_EASING_HORIZONTAL,
    CLOSING_EASING_VERTICAL,
    GESTURE_IS_CLOSING_CLASS,
    MAX_BACKGROUND_BRIGHTNESS,
    MAX_BACKGROUND_PAN_RATIO_HORIZONTAL,
    MAX_BACKGROUND_SCALE,
    MIN_BACKGROUND_BRIGHTNESS,
    MIN_BACKGROUND_SCALE,
    OPENING_DELAY,
    OPENING_DURATION_HORIZONTAL,
    OPENING_DURATION_VERTICAL,
    OPENING_EASING_HORIZONTAL,
    OPENING_EASING_VERTICAL,
} from './modal-gesture.constants';
import { AnimationOptions, Background, SlideInOptions, SlideOutOptions } from './modal-gesture.model';

@Injectable({ providedIn: 'root' })
export class ModalGestureAnimationService {
    readonly #htmlNode = inject(HtmlNode);
    readonly #dispatcher = inject(DispatcherService);
    readonly #modalGestureQueries = inject(ModalGestureQueriesService);
    readonly #modalGestureHelper = inject(ModalGestureHelperService);
    readonly #modalGestureBackgroundService = inject(ModalGestureBackgroundService);
    readonly #modalGestureTransformOriginService = inject(ModalGestureTransformOriginService);
    readonly #viewTransitionUtils = inject(ViewTransitionUtils);
    readonly #appConfig = inject(AppConfig);
    readonly #productMenuService = inject(ProductMenuService);
    readonly #sportsProductUtils = inject(SportsProductUtils);
    readonly #eventsService = inject(EventsService);
    readonly #deviceService = inject(DeviceService);

    readonly #productMenuServiceInitialized$ = this.#productMenuService.initialized.pipe(
        filter((isInitialized) => isInitialized),
        take(1),
    );

    constructor() {
        if (!this.#appConfig.modalDialogGesturesEnabled) return;

        this.initialize();

        this.listenForGestureEvents();

        this.#sportsProductUtils
            .fromSportsEnter()
            .pipe(this.#viewTransitionUtils.deferUntilTransitionReady())
            .subscribe(() => this.initialize());

        this.#sportsProductUtils
            .fromSportsLeave()
            .pipe(this.#viewTransitionUtils.deferUntilTransitionReady())
            .subscribe(() => this.uninitialize());
    }

    private listenForGestureEvents(): void {
        this.#dispatcher.on(ModalGestureEvents.SlideOutToBottom).subscribe(({ modalWindow, teardown }) => {
            this.slideOutToBottom({ modalWindow }).finally(() => {
                teardown?.();
            });
        });
    }

    private initialize(): void {
        this.#htmlNode.setCssClass('gestures', true);

        if (this.#deviceService.isiOS) {
            this.#htmlNode.setCssClass('disable-content-visibility', true);
        }

        this.#productMenuServiceInitialized$.pipe(takeUntil(this.#sportsProductUtils.fromSportsLeave())).subscribe(() => {
            this.#eventsService.raise({
                eventName: VanillaEventNames.ProductMenuScrollStrategyUpdate,
                data: 'noop',
            });
        });
    }

    private uninitialize(): void {
        this.#htmlNode.setCssClass('gestures', false);

        if (this.#deviceService.isiOS) {
            this.#htmlNode.setCssClass('disable-content-visibility', false);
        }

        this.#productMenuServiceInitialized$.pipe(takeUntil(this.#sportsProductUtils.fromSportsEnter())).subscribe(() => {
            this.#eventsService.raise({
                eventName: VanillaEventNames.ProductMenuScrollStrategyUpdate,
                data: 'block',
            });
        });
    }

    async slideInFromRight({ modalWindow, background }: SlideInOptions): Promise<void> {
        this.#modalGestureHelper.setBetslipBarBorderRadius('flat');

        const modalSlideInFromRightAnimation = this.slideModalHorizontal(
            {
                from: `${this.#modalGestureHelper.viewportWidth}px`,
                to: `0px`,
                duration: OPENING_DURATION_HORIZONTAL,
                delay: OPENING_DELAY,
                easing: OPENING_EASING_HORIZONTAL,
            },
            modalWindow,
        )?.finally(() => {
            this.#modalGestureHelper.setBetslipBarBorderRadius('rounded');
        });

        const backgroundSlideInFromRightAnimation = this.slideBackgroundHorizontal(
            {
                from: '0px',
                to: `${this.#modalGestureHelper.viewportWidth * MAX_BACKGROUND_PAN_RATIO_HORIZONTAL}px`,
                fromBrightness: '1',
                toBrightness: `${MIN_BACKGROUND_BRIGHTNESS}`,
                duration: OPENING_DURATION_HORIZONTAL,
                delay: OPENING_DELAY,
                easing: OPENING_EASING_HORIZONTAL,
            },
            background,
        );

        await Promise.allSettled([modalSlideInFromRightAnimation, backgroundSlideInFromRightAnimation]);
    }

    async slideOutToRight({ modalWindow }: SlideOutOptions): Promise<void> {
        const modalSlideRightAnimation = this.slideModalHorizontal(
            {
                from: '0px',
                to: `${this.#modalGestureHelper.viewportWidth}px`,
                duration: CLOSING_DURATION_HORIZONTAL,
                easing: CLOSING_EASING_HORIZONTAL,
            },
            modalWindow,
        );

        const backgroundSlideRightAnimation = this.slideBackgroundHorizontal({
            from: `${this.#modalGestureHelper.viewportWidth * MAX_BACKGROUND_PAN_RATIO_HORIZONTAL}px`,
            to: '0px',
            fromBrightness: `${MIN_BACKGROUND_BRIGHTNESS}`,
            toBrightness: '1',
            duration: CLOSING_DURATION_HORIZONTAL,
            easing: CLOSING_EASING_HORIZONTAL,
        });

        await Promise.allSettled([modalSlideRightAnimation, backgroundSlideRightAnimation]);
    }

    async slideInFromBottom({ modalWindow, background }: SlideInOptions): Promise<void> {
        const modalSlideInFromBottomAnimation = this.slideModalVertical(
            {
                from: `${this.#modalGestureHelper.viewportHeight}px`,
                to: `0px`,
                duration: OPENING_DURATION_VERTICAL,
                easing: OPENING_EASING_VERTICAL,
            },
            modalWindow,
        );

        const zoomBackgroundOutAnimation = this.zoomBackground(
            {
                from: `${MAX_BACKGROUND_SCALE}`,
                to: `${MIN_BACKGROUND_SCALE}`,
                fromBrightness: `${MAX_BACKGROUND_BRIGHTNESS}`,
                toBrightness: `${MIN_BACKGROUND_BRIGHTNESS}`,
                duration: OPENING_DURATION_VERTICAL,
                easing: OPENING_EASING_VERTICAL,
            },
            { isClosing: false },
            background,
        );

        await Promise.allSettled([modalSlideInFromBottomAnimation, zoomBackgroundOutAnimation]);
    }

    async slideOutToBottom({ modalWindow }: SlideOutOptions): Promise<void> {
        modalWindow?.classList.add(GESTURE_IS_CLOSING_CLASS);

        const modalSlideOutToBottomAnimation = this.slideModalVertical(
            {
                from: '0px',
                to: `${this.#modalGestureHelper.viewportHeight}px`,
                duration: CLOSING_DURATION_VERTICAL,
                easing: CLOSING_EASING_VERTICAL,
            },
            modalWindow,
        );

        const zoomBackgroundBackInAnimation = this.zoomBackground(
            {
                from: `${MIN_BACKGROUND_SCALE}`,
                to: `${MAX_BACKGROUND_SCALE}`,
                fromBrightness: `${MIN_BACKGROUND_BRIGHTNESS}`,
                toBrightness: `${MAX_BACKGROUND_BRIGHTNESS}`,
                duration: CLOSING_DURATION_VERTICAL,
                easing: CLOSING_EASING_VERTICAL,
            },
            { isClosing: true },
        );

        await Promise.allSettled([modalSlideOutToBottomAnimation, zoomBackgroundBackInAnimation]);
    }

    slideModalHorizontal({ from, to, duration, delay, easing }: AnimationOptions, modalWindow?: HTMLElement | null): Promise<Animation> | undefined {
        this.#eventsService.raise({ eventName: VanillaEventNames.GestureAnimationStart });

        this.#modalGestureHelper.setEventBackdropTransparent();

        const keyframes: Keyframe[] = [{ transform: `translate3d(${from}, 0, 0)` }, { transform: `translate3d(${to}, 0, 0)` }];

        return (modalWindow ?? this.#modalGestureQueries.getModalWindow())?.animate(keyframes, { duration, delay, easing }).finished.finally(() => {
            this.#eventsService.raise({ eventName: VanillaEventNames.GestureAnimationEnd });
        });
    }

    slideModalVertical(
        { from, to, duration, delay, easing, fill }: AnimationOptions,
        modalWindow?: HTMLElement | null,
    ): Promise<Animation> | undefined {
        this.#eventsService.raise({ eventName: VanillaEventNames.GestureAnimationStart });

        const keyframes: Keyframe[] = [{ transform: `translate3d(0, ${from}, 0)` }, { transform: `translate3d(0, ${to}, 0)` }];

        return (modalWindow ?? this.#modalGestureQueries.getModalWindow())
            ?.animate(keyframes, { duration, delay, easing, fill })
            .finished.finally(() => {
                this.#eventsService.raise({ eventName: VanillaEventNames.GestureAnimationEnd });
            });
    }

    slideBackgroundHorizontal(
        { from, to, fromBrightness, toBrightness, duration, delay, easing }: AnimationOptions,
        background?: Background,
    ): Promise<PromiseSettledResult<Animation>[]> {
        const _background = background ?? this.#modalGestureBackgroundService.getBackground();

        this.#modalGestureHelper.disableConflictingBackgroundAnimations(_background);

        const backgroundSlideAnimations = _background.elements.map((element) => {
            const keyframes: Keyframe[] = [
                {
                    transform: `translate3d(-${from}, 0, 0)`,
                    filter: `brightness(${fromBrightness})`,
                },
                {
                    transform: `translate3d(-${to}, 0, 0)`,
                    filter: `brightness(${toBrightness})`,
                },
            ];

            return element.animate(keyframes, { duration, delay, easing }).finished;
        });

        return Promise.allSettled(backgroundSlideAnimations).finally(() => {
            this.#modalGestureHelper.enableConflictingBackgroundAnimations(_background);
        });
    }

    zoomBackground(
        { from, to, fromBrightness, toBrightness, duration, delay, easing, fill }: AnimationOptions,
        { isClosing = false, updateTransformOrigin = true } = {},
        background?: Background,
    ): Promise<PromiseSettledResult<Animation>[]> | undefined {
        const _background = background ?? this.#modalGestureBackgroundService.getBackground();

        if (_background.type === 'sheet') {
            return;
        }

        if (_background.type === 'event') {
            this.#modalGestureHelper.setEventBackdropOpaque();
        }

        if (updateTransformOrigin) {
            // Transform origin has to be calculated sync here (e.g. not in fastdom/rAF) or the animation could begin first (since composited)
            this.#modalGestureTransformOriginService.updateTransformOrigin(_background);
        }

        this.#modalGestureHelper.disableConflictingBackgroundAnimations(_background);

        const { offset1, offset2 } = isClosing
            ? { offset1: 0, offset2: ANIMATION_COMPLETE_RATIO_VERTICAL }
            : { offset1: 1 - ANIMATION_COMPLETE_RATIO_VERTICAL, offset2: 1 };

        const backgroundAnimations = _background.elements.map((element) => {
            return element?.animate(
                [
                    {
                        transform: `scale3d(${from}, ${from}, 1)`,
                        filter: `brightness(${fromBrightness})`,
                        offset: offset1,
                    },
                    {
                        transform: `scale3d(${to}, ${to}, 1)`,
                        filter: `brightness(${toBrightness})`,
                        offset: offset2,
                    },
                ],
                { duration, delay, easing, fill },
            ).finished;
        });

        return Promise.allSettled(backgroundAnimations).finally(() => {
            this.#modalGestureHelper.enableConflictingBackgroundAnimations(_background);
        });
    }
}
